import { useState, useRef, useEffect, useCallback } from "react";
import { useSupplierVariations } from "../lib/useSupplierVariations";
import { MockupThemeModal } from "../components/mockup/MockupThemeModal";
import { DesignAreaEditor, areaLabelText, type AreaRect } from "../components/mockup/DesignAreaEditor";
import { MockupListsModal } from "../components/mockup/MockupListsModal";
import { CollectionMockupSelector } from "../components/mockup/CollectionMockupSelector";
import OcclusionEditor from "../components/mockup/OcclusionEditor";
import {
  Plus,
  Trash2,
  Save,
  X,
  Loader2,
  Settings2,
  Edit3,
  ImagePlus,
  ArrowRight,
  Eye,
  Sun,
  Moon,
  Palette,
  FolderPlus,
  Folder,
  Check,
  Copy,
  Search,
  Sparkles,
  Star,
  Layers,
  ChevronDown,
} from "lucide-react";
import { CategorySettings } from "../components/mockup/CategorySettings";
import { Link } from "react-router-dom";
import type { CustomMockup, MockupCollection, MockupFolder } from "@erc/shared";
import { hasWarp, warpMapOf } from "@erc/shared";
import { WarpedAreaImage } from "../components/mockup/WarpedAreaImage";
import { MockupAnalysisModal } from "../components/mockup/MockupAnalysisModal";
import { PRESENTATION_LABEL, PRODUCT_TYPE_LABEL } from "../lib/customListing";
import { authHeaders } from "../lib/http";
import { apiClient } from "../lib/api";
import env from "../env";
import { useShopVertical } from "../lib/useShopVertical";

const API_BASE = env.API_URL;

// ======== Label Edit Inline ========

function InlineEdit({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function commit() {
    const trimmed = text.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    setEditing(false);
  }

  if (!editing) {
    return (
      <button
        onClick={() => { setText(value); setEditing(true); }}
        className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 hover:text-indigo-600 transition-colors group"
      >
        <span>{value}</span>
        <Edit3 className="h-3 w-3 text-gray-400 group-hover:text-indigo-500 transition-colors" />
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
      onBlur={commit}
      className="text-sm font-semibold text-gray-800 border border-indigo-300 rounded-lg px-2.5 py-1 w-48 focus:ring-2 focus:ring-indigo-200 focus:outline-none"
    />
  );
}

// ======== Mockup Card ========

const categories = [
  { value: "tshirt", label: "T-Shirt" },
  { value: "hoodie", label: "Hoodie" },
  { value: "mug", label: "Kupa" },
  { value: "tote", label: "Tote Bag" },
  { value: "poster", label: "Poster" },
  { value: "phonecase", label: "Telefon Kılıfı" },
  { value: "custom", label: "Diğer" },
];

function MockupCard({
  mockup,
  onConfigure,
  onEditOcclusion,
  onDelete,
  onUpdateLabel,
  onUpdateCategory,
  onUpdateOpacity,
  testDesignLight,
  testDesignDark,
  selectable,
  selected,
  onToggleSelect,
  folders,
  onMove,
  vars,
  onAssignVariation,
  onToggleSelling,
  onToggleBigPrint,
  onBigPrintCopy,
  isConcept,
  onUpdateConcept,
  onAnalyzeConcept,
  conceptTypes,
  onOpenAnalysis,
  chartPeers,
  onMatchChart,
  onTogglePrintDesign,
}: {
  /** Renk tablosu: alanlar cizili olsa da tasarim basilsin mi */
  onTogglePrintDesign?: (on: boolean) => void;
  /** Renk tablosu: elle eslestirilebilecek diger tablolar */
  chartPeers?: Array<{ id: string; label: string }>;
  onMatchChart?: (target: string | null) => void;
  mockup: CustomMockup;
  onConfigure: () => void;
  onEditOcclusion: () => void;
  onDelete: () => void;
  onUpdateLabel: (label: string) => void;
  onUpdateCategory: (category: string) => void;
  onUpdateOpacity: (opacity: number) => void;
  testDesignLight?: string | null;
  testDesignDark?: string | null;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  folders?: MockupFolder[];
  onMove?: (folderId: string | null) => void;
  vars?: ReturnType<typeof useSupplierVariations>;
  onAssignVariation?: (patch: { brand?: string | null; model?: string | null; color?: string | null }) => void;
  /** "Satan mockup" isaretini tek tikla ac/kapa */
  onToggleSelling?: () => void;
  /** Big print (16 inc DTF) isaretini ac/kapa */
  onToggleBigPrint?: () => void;
  /** Bu mockup'in big print kopyasini olustur */
  onBigPrintCopy?: () => Promise<void>;
  /** Taki hatti: kart tasarim alani/tema yerine SAHNE cozumlemesini gosterir */
  isConcept?: boolean;
  onUpdateConcept?: (patch: { productTypeKey?: string | null; conceptAnalysis?: Record<string, unknown> | null; conceptPrompt?: string | null; sourceUrl?: string | null }) => void;
  onAnalyzeConcept?: (extraPrompt?: string) => Promise<void>;
  /** Kategoriler sekmesinde tanimli taki urun tipleri (yoksa varsayilan liste) */
  conceptTypes?: string[];
  /** Giyim mockup analizi penceresini ac */
  onOpenAnalysis?: () => void;
}) {
  const imgUrl = mockup.imageUrl.startsWith("http") ? mockup.imageUrl : `${API_BASE}${mockup.imageUrl}`;
  // Indented folder options (tree) for the "move to folder" select.
  const folderOptions: { id: string; label: string }[] = (() => {
    if (!folders) return [];
    const byParent: Record<string, MockupFolder[]> = {};
    folders.forEach((f) => { const k = f.parentId ?? "root"; (byParent[k] ||= []).push(f); });
    const out: { id: string; label: string }[] = [];
    const walk = (parent: string, depth: number) => {
      (byParent[parent] || []).forEach((f) => {
        out.push({ id: f.id, label: `${"— ".repeat(depth)}${f.name}` });
        walk(f.id, depth + 1);
      });
    };
    walk("root", 0);
    return out;
  })();
  const designAreas: AreaRect[] = Array.isArray((mockup as any).designAreas) && (mockup as any).designAreas.length > 0
    ? (mockup as any).designAreas
    : (mockup.designAreaW > 20 && mockup.designAreaH > 20
        ? [{ id: "legacy", x: mockup.designAreaX, y: mockup.designAreaY, w: mockup.designAreaW, h: mockup.designAreaH, rotation: mockup.designAreaRotation || 0, label: "on" }]
        : []);
  const hasDesignArea = designAreas.length > 0;
  const [bpBusy, setBpBusy] = useState(false);
  // Theme follows the area editor (first area's theme) as the single source of truth.
  const isDark = (((designAreas[0] as any)?.theme as string | undefined) ?? mockup.theme) === "dark";
  // A multi-area mockup can mix light & dark areas (e.g. a sheet of shirts in
  // different colors) — show "Karisik" instead of a single light/dark badge.
  const areaThemes = designAreas.map((a) => ((a as any).theme as string | undefined) ?? mockup.theme);
  const mixedTheme = designAreas.length > 1 && new Set(areaThemes).size > 1;

  return (
    <div className={`rounded-xl border bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow group ${selected ? "border-indigo-400 ring-2 ring-indigo-200" : "border-gray-200"}`}>
      {/* Preview */}
      <div className={`relative flex items-center justify-center ${isDark ? "bg-gray-800" : "bg-gray-100"}`} style={{ height: 220 }}>
        {selectable && (
          <button
            onClick={onToggleSelect}
            className={`absolute top-2 left-2 z-10 flex items-center justify-center w-6 h-6 rounded-full border-2 transition-colors ${
              selected ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white/80 border-gray-300 hover:border-indigo-400"
            }`}
          >
            {selected && <Check className="h-3.5 w-3.5" />}
          </button>
        )}
        {/* Satan mockup: uretilen listenin ANA gorseli olur. Tek tikla ac/kapa. */}
        {onToggleSelling && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleSelling(); }}
            title={mockup.isSelling ? "Satan mockup — kaldırmak için tıkla" : "Satan mockup olarak işaretle (üretilen listenin ana görseli)"}
            className={`absolute top-2 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors ${
              mockup.isSelling
                ? "border-amber-500 bg-amber-500 text-white"
                : "border-gray-300 bg-white/80 text-gray-400 hover:border-amber-400 hover:text-amber-500"
            }`}
          >
            <Star className={`h-3.5 w-3.5 ${mockup.isSelling ? "fill-current" : ""}`} />
          </button>
        )}
        <div className="relative h-full" style={{ aspectRatio: `${mockup.imageWidth} / ${mockup.imageHeight}` }}>
          <img src={imgUrl} alt={mockup.label} className="absolute inset-0 h-full w-full" crossOrigin="anonymous" />
          {designAreas.map((area, idx) => {
            const colors = ["border-indigo-400", "border-emerald-400", "border-amber-400"];
            const areaLabel = areaLabelText(area.label);
            const areaIsDark = (((area as any).theme as string | undefined) ?? mockup.theme) === "dark";
            const testSrc = areaIsDark ? testDesignDark : testDesignLight;
            // Carpitilmis alan: render ile ayni esleme (duz kutu yaniltiyordu)
            if (testSrc && hasWarp(area)) {
              return <WarpedAreaImage key={area.id || idx} area={area} src={testSrc} imageW={mockup.imageWidth} imageH={mockup.imageHeight} opacity={mockup.opacity / 100} />;
            }
            return (
              <div key={area.id || idx}
                className={`absolute border-2 border-dashed rounded pointer-events-none ${colors[idx] || colors[0]} overflow-hidden`}
                style={{
                  left: `${(area.x / mockup.imageWidth) * 100}%`,
                  top: `${(area.y / mockup.imageHeight) * 100}%`,
                  width: `${(area.w / mockup.imageWidth) * 100}%`,
                  height: `${(area.h / mockup.imageHeight) * 100}%`,
                  transform: area.rotation ? `rotate(${area.rotation}deg)` : undefined,
                  transformOrigin: "top left",
                  opacity: mockup.opacity / 100,
                }}
              >
                {(areaIsDark ? testDesignDark : testDesignLight) ? (
                  <div className="w-full h-full">
                    <img src={(areaIsDark ? testDesignDark : testDesignLight) as string} alt="test" className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-[9px] font-bold uppercase leading-none text-center px-0.5" style={{
                      color: areaIsDark ? "#000000" : "#ffffff",
                      textShadow: areaIsDark ? "0 0 2px rgba(255,255,255,0.7)" : "0 0 2px rgba(0,0,0,0.7)",
                    }}>{areaLabel}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="absolute top-2 right-2 flex gap-1.5">
          {isConcept ? (
            <>
              {/* Takida tema ve tasarim alani yok: sahnenin urun tipi ve
                  cozumlenip cozulmedigi onemli */}
              <span className="rounded-full border border-pink-200 bg-pink-50 px-2 py-0.5 text-[10px] font-medium text-pink-700">
                {mockup.productTypeKey || "ürün tipi yok"}
              </span>
              {(mockup as unknown as { conceptAnalysis?: unknown }).conceptAnalysis ? (
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                  Çözümlendi
                </span>
              ) : (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                  Çözümlenmedi
                </span>
              )}
            </>
          ) : (
          <>
          <span
            title="Açık/Koyu — Alanı Düzenle'de ayarlanır"
            className={`flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${
              mixedTheme ? "bg-gradient-to-r from-white to-gray-700 text-gray-700 border-gray-300"
                : isDark ? "bg-gray-700 text-gray-200 border-gray-600" : "bg-white text-gray-600 border-gray-200"
            }`}
          >
            {mixedTheme ? <><Sun className="h-3 w-3" /><Moon className="h-3 w-3" /></> : isDark ? <Moon className="h-3 w-3" /> : <Sun className="h-3 w-3" />}
            {mixedTheme ? "Karışık" : isDark ? "Koyu" : "Açık"}
          </span>
          {hasDesignArea ? (
            <span className="text-[10px] font-medium bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">
              {designAreas.length} Alan
            </span>
          ) : (
            <span className="text-[10px] font-medium bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">Alan Yok</span>
          )}
          </>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-4 space-y-3">
        <InlineEdit value={mockup.label} onSave={onUpdateLabel} />

        {/* Giyim mockup analizi: urun tipi, sunum, urunler; Custom Liste Studyosu oneriyi bununla yapar */}
        {!isConcept && onOpenAnalysis && (
          <button onClick={onOpenAnalysis} title="Mockup analizini gör, düzenle ve onayla"
            className="flex w-full items-center gap-1.5 rounded-lg border border-gray-200 px-2 py-1.5 text-left text-xs hover:bg-gray-50">
            {mockup.mockupAnalysis ? (
              <>
                <span className="truncate text-gray-700">
                  {PRODUCT_TYPE_LABEL[mockup.mockupAnalysis.productType] ?? mockup.mockupAnalysis.productType} · {PRESENTATION_LABEL[mockup.mockupAnalysis.presentation] ?? mockup.mockupAnalysis.presentation} · {mockup.mockupAnalysis.items.length} ürün
                </span>
                <span className={`ml-auto flex-shrink-0 rounded-full px-2 py-0.5 font-medium ${mockup.mockupAnalysis.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {mockup.mockupAnalysis.status === "approved" ? "Onaylı" : "Taslak"}
                </span>
              </>
            ) : <span className="text-gray-500">Analiz yok · AI ile analiz et</span>}
          </button>
        )}

        <div className="flex items-center gap-2">
          {/* Tekstil kategorisi (tisort, kupa...) takida anlamsiz: orada urun
              tipi (kolye, yuzuk...) kullanilir, asagidaki panelde secilir. */}
          {!isConcept && (
          <select
            value={mockup.category}
            onChange={(e) => onUpdateCategory(e.target.value)}
            className="text-xs text-gray-500 border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50 hover:bg-gray-100 transition-colors focus:ring-2 focus:ring-indigo-200 focus:outline-none"
          >
            {categories.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          )}
          <span className="text-[10px] text-gray-400 ml-auto">{mockup.imageWidth}x{mockup.imageHeight}</span>
        </div>

        {/* Marka / Model / Renk (tedarikci katalogundan, elle ata — kademeli).
            Takida karsiligi yok: urun tedarikciden gelir, sahnede secilmez. */}
        {!isConcept && vars?.hasCatalog && onAssignVariation && (
          <div className="grid grid-cols-3 gap-1.5">
            <select
              value={mockup.brand || ""}
              onChange={(e) => onAssignVariation({ brand: e.target.value || null, model: null, color: null })}
              title="Marka ata"
              className="min-w-0 text-[11px] text-gray-600 border border-gray-200 rounded-lg px-1.5 py-1.5 bg-gray-50 hover:bg-gray-100 focus:ring-2 focus:ring-indigo-200 focus:outline-none"
            >
              <option value="">Marka</option>
              {vars.brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <select
              value={mockup.model || ""}
              onChange={(e) => onAssignVariation({ model: e.target.value || null, color: null })}
              title="Model ata"
              className="min-w-0 text-[11px] text-gray-600 border border-gray-200 rounded-lg px-1.5 py-1.5 bg-gray-50 hover:bg-gray-100 focus:ring-2 focus:ring-indigo-200 focus:outline-none"
            >
              <option value="">Model</option>
              {vars.modelsForBrand(mockup.brand || "").map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
              value={mockup.color || ""}
              onChange={(e) => onAssignVariation({ color: e.target.value || null })}
              title="Renk ata (stoktaki renkler)"
              className="min-w-0 text-[11px] text-gray-600 border border-gray-200 rounded-lg px-1.5 py-1.5 bg-gray-50 hover:bg-gray-100 focus:ring-2 focus:ring-indigo-200 focus:outline-none"
            >
              <option value="">Renk</option>
              {vars.colorsFor(mockup.brand || "", mockup.model || "").map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        {/* Renk tablosu: tasarim basma anahtari (kapaliyken her listede duz tablo) */}
        {onTogglePrintDesign && (
          <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-700" title="Kapalıyken bu tablo, alanları çizili olsa da her listede düz gider (listedeki 'Açık' ayarı hariç)">
            <input type="checkbox" className="accent-indigo-600" checked={(mockup as { printDesign?: boolean }).printDesign !== false}
              onChange={(e) => onTogglePrintDesign(e.target.checked)} />
            Tasarım bas
          </label>
        )}

        {/* Renk tablosu: ayni tabloyu gosteren baska tabloyla eslestir (alanlar ortak) */}
        {chartPeers && onMatchChart && (
          <select value="" onChange={(e) => { const v = e.target.value; if (v) onMatchChart(v === "__unmatch" ? null : v); }}
            aria-label="Renk tablosunu eşleştir" title="Aynı tabloyu gösteren başka bir tabloyla eşleştir: tek kart görünür, birine çizilen alanlar ikisinde de kullanılır"
            className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-600 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
            <option value="">Eşleştir…</option>
            {chartPeers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            {/\(\+ /.test(mockup.label) && <option value="__unmatch">Eşleştirmeyi kaldır</option>}
          </select>
        )}

        {/* Move to folder */}
        {folders && onMove && (
          <div className="flex items-center gap-2">
            <Folder className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <select
              value={mockup.folderId ?? ""}
              onChange={(e) => onMove(e.target.value || null)}
              title="Klasöre taşı"
              className="flex-1 text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50 hover:bg-gray-100 focus:ring-2 focus:ring-indigo-200 focus:outline-none"
            >
              <option value="">Kök (klasörsüz)</option>
              {folderOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
        )}

        {/* Opacity slider — tasarim bindirme icin; takida anlami yok */}
        {!isConcept && (
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-gray-500 font-medium whitespace-nowrap">Opaklık</label>
          <input
            type="range"
            min={10}
            max={100}
            value={mockup.opacity}
            onChange={(e) => onUpdateOpacity(Number(e.target.value))}
            className="flex-1 h-1.5 accent-indigo-600"
          />
          <span className="text-[10px] text-gray-500 w-8 text-right">%{mockup.opacity}</span>
        </div>
        )}

        {/* Big print (16 inc DTF): bu mockup listede secilirse liste big print olur */}
        {!isConcept && (onToggleBigPrint || onBigPrintCopy) && (
          <div className="flex items-center gap-1.5">
            {onToggleBigPrint && (
              <button type="button" onClick={onToggleBigPrint}
                title={mockup.bigPrint
                  ? "Big print mockup — listede seçilirse diğer mockuplar big print kopyalarıyla değişir, fiyata +$5 eklenir. Kaldırmak için tıkla"
                  : "Bu mockup big print (16 inç) için kurulduysa işaretle"}
                className={`rounded-md px-2 py-1 text-[10px] font-semibold transition-colors ${mockup.bigPrint ? "bg-orange-500 text-white hover:bg-orange-600" : "border border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
                {mockup.bigPrint ? "Big print ✓" : "Big print"}
              </button>
            )}
            {onBigPrintCopy && !mockup.bigPrint && (
              <button type="button" disabled={bpBusy}
                onClick={async () => { setBpBusy(true); try { await onBigPrintCopy(); } finally { setBpBusy(false); } }}
                title="Aynı fotoğraftan big print (16 inç) kopyası oluştur: maske ve ışık ayarları kopyalanır, alan 16 inçe genişler"
                className="rounded-md border border-orange-200 px-2 py-1 text-[10px] font-medium text-orange-700 hover:bg-orange-50 disabled:opacity-50">
                {bpBusy ? "Kopyalanıyor..." : "Big print kopyası oluştur"}
              </button>
            )}
          </div>
        )}

        {/* Taki: sahne cozumlemesi — analiz et, duzenle, elle gir */}
        {isConcept && onUpdateConcept && (
          <ConceptAnalysisPanel
            mockup={mockup}
            onSave={onUpdateConcept}
            onAnalyze={onAnalyzeConcept}
            types={conceptTypes}
          />
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          {isConcept ? (
            <>
              <Link
                to="/jewelry-studio"
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-pink-50 px-3 py-2 text-xs font-medium text-pink-700 transition-colors hover:bg-pink-100"
              >
                <Sparkles className="h-3.5 w-3.5" /> Studio
              </Link>
              <button
                onClick={onDelete}
                className="flex items-center justify-center rounded-lg bg-gray-50 px-2.5 py-2 text-xs text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                title="Sil"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
          <>
          <button
            onClick={onConfigure}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
          >
            <Settings2 className="h-3.5 w-3.5" />
            {hasDesignArea ? "Alanı Düzenle" : "Alan Belirle"}
          </button>
          <button
            onClick={onEditOcclusion}
            title="Tasarımın önünde kalacak yerleri işaretle (saç, kumaş kıvrımı)"
            className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
              mockup.foregroundUrl
                ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <Layers className="h-3.5 w-3.5" />
            {mockup.foregroundUrl ? "Ön Plan ✓" : "Ön Plan"}
          </button>
          <Link
            to="/mockup-studio"
            className="flex items-center justify-center gap-1.5 rounded-lg bg-purple-50 px-3 py-2 text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors"
          >
            <Palette className="h-3.5 w-3.5" />
            Studio
          </Link>
          <Link
            to="/mockup-studio"
            className="flex items-center justify-center gap-1.5 rounded-lg bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Eye className="h-3.5 w-3.5" />
          </Link>
          <button
            onClick={onDelete}
            className="flex items-center justify-center rounded-lg bg-gray-50 px-2.5 py-2 text-xs text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
            title="Sil"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

// ======== Konsept cozumlemesi (taki) ========
// AI bir kez cozumler; sonuc BURADA duzenlenebilir ve elle de girilebilir.
// Uretim prompt'u bu alanlardan beslendigi icin duzeltmeler dogrudan sonuca yansir.
const CONCEPT_FIELDS: Array<{ key: string; label: string; hint: string }> = [
  { key: "summary", label: "Özet", hint: "sahnenin kısa tarifi" },
  { key: "placement", label: "Yerleşim", hint: "takının yeri ve ölçeği" },
  { key: "pose", label: "Poz", hint: "manken/el pozu, açı" },
  { key: "framing", label: "Kadraj", hint: "örn. omuz üstü yakın plan" },
  { key: "lighting", label: "Işık", hint: "yön, sertlik, renk sıcaklığı" },
  { key: "background", label: "Arka plan", hint: "zemin ve doku" },
  { key: "styling", label: "Styling", hint: "kıyafet, saç, diğer aksesuar" },
  { key: "risks", label: "Riskler", hint: "ürün değişiminde bozulabilecek yerler" },
];

const JEWELRY_TYPES = ["kolye", "yuzuk", "kupe", "bileklik", "halhal", "set"];

function ConceptAnalysisPanel({ mockup, onSave, onAnalyze, types }: {
  mockup: CustomMockup;
  onSave: (patch: { productTypeKey?: string | null; conceptAnalysis?: Record<string, unknown> | null; conceptPrompt?: string | null; sourceUrl?: string | null }) => void;
  onAnalyze?: (extraPrompt?: string) => Promise<void>;
  types?: string[];
}) {
  const typeOptions = types && types.length > 0 ? types : JEWELRY_TYPES;
  const stored = ((mockup as unknown as { conceptAnalysis?: Record<string, unknown> | null }).conceptAnalysis ?? null);
  const storedPrompt = (mockup as unknown as { conceptPrompt?: string | null }).conceptPrompt ?? "";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [prompt, setPrompt] = useState(storedPrompt);
  // Analize ek istek: "zincir uzunlugunu da tarif et", "modelin teni
  // degismesin" gibi. Her konseptin ihtiyaci farkli oluyor.
  const [analyzeExtra, setAnalyzeExtra] = useState("");
  // Sahnenin alindigi rakip liste adresi. Konsepte bakarken "bu nereden
  // geldi" sorusuna cevap verir; eski kayitlar icin elle girilebilir.
  const storedSource = (mockup as unknown as { sourceUrl?: string | null }).sourceUrl ?? "";
  const [srcUrl, setSrcUrl] = useState(storedSource);

  useEffect(() => { setSrcUrl(storedSource); }, [storedSource]);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const f of CONCEPT_FIELDS) {
      const v = stored?.[f.key];
      next[f.key] = typeof v === "string" ? v : "";
    }
    setDraft(next);
    setPrompt(storedPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockup.id, stored, storedPrompt]);

  async function analyze() {
    if (!onAnalyze) return;
    setBusy(true);
    try { await onAnalyze(analyzeExtra.trim() || undefined); } finally { setBusy(false); }
  }

  function save() {
    // Bos alanlar yazilmaz: elle silinen alan cozumlemeden de dusmeli
    const next: Record<string, unknown> = { ...(stored ?? {}) };
    for (const f of CONCEPT_FIELDS) {
      const v = draft[f.key]?.trim();
      if (v) next[f.key] = v; else delete next[f.key];
    }
    onSave({ conceptAnalysis: Object.keys(next).length ? next : null, conceptPrompt: prompt.trim() || null });
    setOpen(false);
  }

  return (
    <div className="rounded-lg border border-pink-100 bg-pink-50/40 p-2">
      <div className="flex items-center gap-1.5">
        <select
          value={mockup.productTypeKey || ""}
          onChange={(e) => onSave({ productTypeKey: e.target.value || null })}
          title="Bu sahne hangi takı türü için?"
          className="min-w-0 flex-1 rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px] text-gray-700"
        >
          <option value="">Ürün tipi seç…</option>
          {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {onAnalyze && (
          <button
            onClick={analyze}
            disabled={busy}
            title="Sahneyi AI ile çözümle (poz, kadraj, ışık, takının yeri)"
            className="flex items-center gap-1 rounded bg-pink-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-pink-700 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {stored ? "Yeniden" : "Analiz"}
          </button>
        )}
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50"
        >
          {open ? "Kapat" : "Düzenle"}
        </button>
      </div>

      {/* Kaynak urun: sahne hangi listeden alindi (eklenti kaydeder, elle de girilir) */}
      <div className="mt-1.5 flex items-center gap-1.5">
        <input
          value={srcUrl}
          onChange={(e) => setSrcUrl(e.target.value)}
          onBlur={() => { if (srcUrl.trim() !== storedSource) onSave({ sourceUrl: srcUrl.trim() || null }); }}
          placeholder="Kaynak ürün linki"
          className="min-w-0 flex-1 rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px] text-gray-700"
        />
        {srcUrl.trim() && (
          <a href={srcUrl} target="_blank" rel="noreferrer" title="Kaynak ürünü aç"
            className="shrink-0 rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px] text-indigo-600 hover:bg-indigo-50">
            Aç
          </a>
        )}
      </div>

      {/* Ek istek: analiz calistirilmadan once yazilir, yalnizca o calistirmaya
          uygulanir (kalici degil). */}
      <input
        value={analyzeExtra}
        onChange={(e) => setAnalyzeExtra(e.target.value)}
        placeholder="Analize ek istek (ops.): örn. zincir uzunluğunu da tarif et"
        className="mt-1 w-full rounded border border-gray-200 px-1.5 py-1 text-[10px]"
      />

      {!open && typeof stored?.summary === "string" && stored.summary && (
        <p className="mt-1 line-clamp-2 text-[10px] text-gray-600">{stored.summary}</p>
      )}

      {open && (
        <div className="mt-2 space-y-1.5">
          {CONCEPT_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] font-medium text-gray-500">{f.label}</label>
              <input
                value={draft[f.key] ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                placeholder={f.hint}
                className="w-full rounded border border-gray-200 px-1.5 py-1 text-[11px]"
              />
            </div>
          ))}
          <div>
            <label className="block text-[10px] font-medium text-gray-500">Üretim talimatı (İngilizce)</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={2}
              placeholder="Keep the scene; place the product exactly as in the reference…"
              className="w-full rounded border border-gray-200 px-1.5 py-1 text-[11px]"
            />
          </div>
          <div className="flex gap-1.5">
            <button onClick={save} className="flex-1 rounded bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-700">
              Kaydet
            </button>
            <button onClick={() => setOpen(false)} className="rounded border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50">
              İptal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ======== Collection Card ========

function CollectionCard({
  collection,
  onDelete,
  onRename,
  onEditMockups,
  productTypes,
  onSetProductType,
  selected,
  onToggleSelect,
}: {
  collection: MockupCollection;
  allMockups: CustomMockup[];
  onDelete: () => void;
  onRename: (name: string) => void;
  onEditMockups: () => void;
  productTypes: ProductTypeProfile[];
  onSetProductType: (key: string) => void;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const previewMockups = collection.mockups.slice(0, 4);
  const hasType = !!collection.productTypeKey;

  return (
    <div className={`overflow-hidden rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md ${selected ? "border-indigo-400 ring-2 ring-indigo-200" : "border-gray-200"}`}>
      {/* Toplu islem secimi */}
      <label className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-1.5 text-[11px] text-gray-500 hover:bg-gray-50">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} className="h-3.5 w-3.5" />
        Toplu işlem için seç
      </label>

      {/* Preview grid (click to edit collection) */}
      <div onClick={onEditMockups} className="grid grid-cols-2 gap-0.5 bg-gray-200 cursor-pointer" style={{ height: 180 }} title="Mockup ekle/çıkar">
        {previewMockups.map((m) => {
          const _tu = m.thumbnailUrl || m.imageUrl; const url = _tu.startsWith("http") ? _tu : `${API_BASE}${_tu}`;
          return (
            <div key={m.id} className="bg-gray-100 flex items-center justify-center overflow-hidden">
              <img src={url} alt={m.label} className="max-h-full max-w-full object-contain" crossOrigin="anonymous" />
            </div>
          );
        })}
        {Array.from({ length: Math.max(0, 4 - previewMockups.length) }).map((_, i) => (
          <div key={`empty-${i}`} className="bg-gray-50 flex items-center justify-center">
            <ImagePlus className="h-6 w-6 text-gray-200" />
          </div>
        ))}
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <InlineEdit value={collection.name} onSave={onRename} />
          <span className="text-xs text-gray-400">{collection.mockups.length} mockup</span>
        </div>

        {/* Urun tipi: bu grup neyin mockup'lari. Studio'da coklu grup uretiminde
            her grup KENDI tipiyle ayri taslak olusturur. */}
        <div>
          <label className="mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Ürün tipi
          </label>
          <select
            value={collection.productTypeKey ?? ""}
            onChange={(e) => onSetProductType(e.target.value)}
            className={`w-full rounded-lg border px-2 py-1.5 text-xs ${
              hasType ? "border-gray-200 text-gray-700" : "border-amber-300 bg-amber-50 text-amber-700"
            }`}
          >
            <option value="">Atanmamış</option>
            {productTypes.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onEditMockups}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
          >
            <Edit3 className="h-3.5 w-3.5" />
            Mockup Seç
          </button>
          <Link
            to="/mockup-studio"
            className="flex items-center justify-center gap-1.5 rounded-lg bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Eye className="h-3.5 w-3.5" />
            Studio
          </Link>
          <button
            onClick={onDelete}
            className="flex items-center justify-center rounded-lg bg-gray-50 px-2.5 py-2 text-xs text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
            title="Sil"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ======== Collection Mockup Selector Modal ========

// ======== Main Page ========

type Tab = "mockups" | "collections" | "categories";

// Aktif urun tipi profilleri (tshirt, mug, ornament...) — gruplari etiketlemek icin
export interface ProductTypeProfile { key: string; label: string; vertical: string }

export default function MockupManagerPage() {
  const { mockupKind, isConceptVertical, vertical } = useShopVertical();
  // Yukleme hatasi (dosya basina neden) — sessiz basarisizligi bitirir
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  // Taki: urun tipine gore suzme (kolye, yuzuk...)
  const [listFilterType, setListFilterType] = useState<string>("");
  // Taki dikeyinde varlik "mockup" degil "konsept fotografi" — etiketler
  // sayfa basligiyla ayni dili konussun (Konsept Fotograflari sayfasi).
  const NOUN = isConceptVertical ? "Konsept" : "Mockup";
  const NOUNS = isConceptVertical ? "Konseptler" : "Mockuplar";
  const [tab, setTab] = useState<Tab>("mockups");
  const [showMockupLists, setShowMockupLists] = useState(false);
  const [mockups, setMockups] = useState<CustomMockup[]>([]);
  const [collections, setCollections] = useState<MockupCollection[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeProfile[]>([]);
  // Toplu urun tipi atama
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<Set<string>>(new Set());
  const [bulkType, setBulkType] = useState("");
  const [folders, setFolders] = useState<MockupFolder[]>([]); // independent mockup folders
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [configuringMockup, setConfiguringMockup] = useState<CustomMockup | null>(null);
  const [occlusionMockup, setOcclusionMockup] = useState<CustomMockup | null>(null);
  const [applyAreas, setApplyAreas] = useState<{ areas: AreaRect[]; sourceW: number; sourceH: number; items: { mockup: CustomMockup; selected: boolean; theme: "light" | "dark" }[] } | null>(null);
  const [applyEditMockup, setApplyEditMockup] = useState<CustomMockup | null>(null); // edit one mockup's area from the apply modal
  const [applyFilterQ, setApplyFilterQ] = useState("");
  const [applyFilterArea, setApplyFilterArea] = useState<"all" | "has" | "none">("all");
  const [listFilterQ, setListFilterQ] = useState("");
  const [listFilterArea, setListFilterArea] = useState<"all" | "has" | "none">("all");
  const [listFilterBrand, setListFilterBrand] = useState("");
  const [listFilterModel, setListFilterModel] = useState("");
  const [listFilterColor, setListFilterColor] = useState("");
  // Renk adi etiketi eklenmis / eklenmemis mockuplar
  const [listFilterLabel, setListFilterLabel] = useState<"" | "has" | "none">("");
  // Color chart: liste sablonlarindaki renk tablolari (sunucu sablonlardan kendiliginden ekler)
  const [chartMode, setChartMode] = useState(false);
  const [charts, setCharts] = useState<CustomMockup[] | null>(null);
  useEffect(() => {
    if (!chartMode || charts) return;
    apiClient.listMockups("chart").then((d) => setCharts(d ?? [])).catch(() => setCharts([]));
  }, [chartMode, charts]);
  // Tedarikci katalogu: marka/model/renk (stok) kademeli — mockup varyasyon atamasi
  // Marka/model/renk filtreleri aktif dikeyin katalogundan gelsin — taki
  // magazasinda Gildan/Bella Canvas listelenmemeli
  const supplierVars = useSupplierVariations(vertical);
  const [editingCollection, setEditingCollection] = useState<MockupCollection | null>(null);
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null); // null = root
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [testDesignLight, setTestDesignLight] = useState<string | null>(() => { try { return localStorage.getItem("erc_test_design_light"); } catch { return null; } });
  const [testDesignDark, setTestDesignDark] = useState<string | null>(() => { try { return localStorage.getItem("erc_test_design_dark"); } catch { return null; } });
  const [testDesignShared, setTestDesignShared] = useState(true); // tek tasarım ikisi için
  const [testUploadTarget, setTestUploadTarget] = useState<"light" | "dark">("light");
  const [autoDetecting, setAutoDetecting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const testDesignInputRef = useRef<HTMLInputElement>(null);
  const opacityTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Bir mockup'a marka/model/renk ata (elle) — iyimser guncelleme + sunucuya yaz
  const handleAssignVariation = useCallback(async (id: string, patch: { brand?: string | null; model?: string | null; color?: string | null }) => {
    setMockups((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    try {
      await apiClient.updateMockup(id, patch);
    } catch { /* ignore */ }
  }, []);

  // Toplu marka/model/renk atama
  const [bulkSel, setBulkSel] = useState<Set<string>>(new Set());
  // Giyim mockup analizi penceresi
  const [analysisMockup, setAnalysisMockup] = useState<CustomMockup | null>(null);
  // Secilenleri arka planda AI ile analiz ettir; bitenler yenilemeyle gelir
  async function handleBulkAnalyze() {
    const ids = [...bulkSel];
    if (!ids.length) return;
    const r = await fetch(`${API_BASE}/api/mockups/analyze-bulk`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ ids }) });
    const j = await r.json().catch(() => ({}));
    alert(r.ok ? `${ids.length} mockup analiz kuyruğuna alındı. Her biri birkaç saniye sürer; sayfayı yenileyince sonuçlar görünür.` : (j.error?.message ?? j.error ?? "Analiz başlatılamadı"));
  }
  const [bulkProductType, setBulkProductType] = useState("");
  // Listeyi yalnizca "satan mockup" isaretlilere daralt
  const [onlySelling, setOnlySelling] = useState(false);
  // Giyim mockup analizine gore suzgec ve gruplu gorunum (klasorlerden bagimsiz)
  const [groupByAnalysis, setGroupByAnalysis] = useState<boolean>(() => { try { return localStorage.getItem("erc:mockups:group-analysis") === "1"; } catch { return false; } });
  const toggleGroupByAnalysis = () => setGroupByAnalysis((v) => { try { localStorage.setItem("erc:mockups:group-analysis", v ? "0" : "1"); } catch { /* depolama kapali */ } return !v; });
  const [listFilterAnalysis, setListFilterAnalysis] = useState<"" | "analyzed" | "none" | "approved" | "draft">("");
  const [listFilterPType, setListFilterPType] = useState("");
  const [listFilterPres, setListFilterPres] = useState("");
  const [listFilterCount, setListFilterCount] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set(["__none__"]));
  const [bulkBrand, setBulkBrand] = useState("");
  const [bulkModel, setBulkModel] = useState("");
  const [bulkColor, setBulkColor] = useState("");
  const [bulkApplying, setBulkApplying] = useState(false);
  const [themeModalSources, setThemeModalSources] = useState<CustomMockup[] | null>(null);
  const toggleBulk = useCallback((id: string) => {
    setBulkSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  async function applyBulkVariation() {
    const patch: { brand?: string | null; model?: string | null; color?: string | null } = {};
    if (bulkBrand) patch.brand = bulkBrand;
    if (bulkModel) patch.model = bulkModel;
    if (bulkColor) patch.color = bulkColor;
    const ids = [...bulkSel];
    if (ids.length === 0 || Object.keys(patch).length === 0) return;
    setBulkApplying(true);
    setMockups((prev) => prev.map((m) => (bulkSel.has(m.id) ? { ...m, ...patch } : m)));
    try {
      await apiClient.bulkAssignMockupVariation(ids, patch);
    } catch { fetchMockups(); }
    setBulkApplying(false);
    setBulkSel(new Set());
    setBulkBrand(""); setBulkModel(""); setBulkColor("");
  }

  /** Ornek tasarim yalnizca ONIZLEME icin: tarayicida saklanabilecek boyuta
   *  indirilir. Ham PNG'ler cogu zaman 5-10 MB; base64'e cevrilince
   *  localStorage kotasini asiyor, yazma sessizce dusuyor ve sayfa
   *  yenilenince tasarim KAYBOLUYORDU. */
  async function shrinkForStorage(file: File): Promise<string> {
    const raw = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = (ev) => resolve(ev.target?.result as string);
      r.onerror = () => reject(new Error("Dosya okunamadı"));
      r.readAsDataURL(file);
    });
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new window.Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error("Görsel açılamadı"));
        i.src = raw;
      });
      const MAX = 1400; // onizleme icin fazlasiyla yeterli
      const scale = Math.min(1, MAX / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return raw;
      ctx.drawImage(img, 0, 0, w, h);
      // Saydamlik korunmali (tasarimlar PNG) — WebP saydamligi destekler
      const out = canvas.toDataURL("image/webp", 0.85);
      return out.length < raw.length ? out : raw;
    } catch {
      return raw; // kucultulemezse ham hali kullanilir
    }
  }

  async function handleTestDesignUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (testDesignInputRef.current) testDesignInputRef.current.value = "";
    if (!file) return;
    setUploadError(null);
    const url = await shrinkForStorage(file);

    // Kota asilirsa SESSIZ kalma: tasarim bu oturumda gorunur ama yenileyince
    // gider — kullanici bunu bilmeli.
    let quotaFailed = false;
    const persist = (key: string, val: string) => {
      try { localStorage.setItem(key, val); } catch { quotaFailed = true; }
    };

    if (testDesignShared) {
      setTestDesignLight(url); setTestDesignDark(url);
      persist("erc_test_design_light", url); persist("erc_test_design_dark", url);
    } else if (testUploadTarget === "light") {
      setTestDesignLight(url); persist("erc_test_design_light", url);
    } else {
      setTestDesignDark(url); persist("erc_test_design_dark", url);
    }

    if (quotaFailed) {
      // Yer acmayi dene: eski kayit varsa silip bir kez daha yaz
      try {
        localStorage.removeItem("erc_test_design_light");
        localStorage.removeItem("erc_test_design_dark");
        localStorage.setItem("erc_test_design_light", url);
        if (testDesignShared) localStorage.setItem("erc_test_design_dark", url);
        quotaFailed = false;
      } catch { /* yine olmadi */ }
    }
    if (quotaFailed) {
      setUploadError("Örnek tasarım tarayıcı depolamasina sığmadı — bu oturumda görünür, sayfayı yenileyince kaybolur. Daha küçük bir dosya deneyin.");
    }
  }

  function clearTestDesigns() {
    setTestDesignLight(null);
    setTestDesignDark(null);
    setTestDesignShared(true);
    try { localStorage.removeItem("erc_test_design_light"); localStorage.removeItem("erc_test_design_dark"); } catch { /* ignore */ }
  }

  // Magazanin dikeyi gorsel varliklarini ayirir: taki magazasinda tekstil
  // mockuplari, tekstil magazasinda konsept fotograflari listelenmez.
  const fetchMockups = useCallback(async () => {
    try {
      const data = await apiClient.listMockups(mockupKind);
      setMockups(data ?? []);
    } catch { /* ignore */ }
  }, [mockupKind]);

  const fetchCollections = useCallback(async () => {
    try {
      const data = await apiClient.listMockupCollections(mockupKind);
      setCollections(data ?? []);
    } catch { /* ignore */ }
  }, [mockupKind]);

  const fetchProductTypes = useCallback(async () => {
    try {
      // Kategoriler sekmesi de dikeye baglidir: taki magazasinda tisort
      // urun tipleri listelenmemeli
      const res = await fetch(`${API_BASE}/api/product-types?vertical=${vertical}`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const json = await res.json();
      setProductTypes(json.data ?? []);
    } catch { /* ignore */ }
  }, [vertical]);

  // Tek gruba urun tipi ata (iyimser guncelleme + sunucuya yaz)
  const setCollectionProductType = useCallback(async (id: string, key: string) => {
    setCollections((prev) => prev.map((c) => (c.id === id ? { ...c, productTypeKey: key || null } : c)));
    try {
      await fetch(`${API_BASE}/api/mockups/collections/${id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ productTypeKey: key || null }),
      });
    } catch { /* ag hatasi — sonraki yuklemede sunucudaki deger gecerli */ }
  }, []);

  const fetchFolders = useCallback(async () => {
    try {
      const data = await apiClient.listMockupFolders(mockupKind);
      setFolders(data ?? []);
    } catch { /* ignore */ }
  }, [mockupKind]);

  useEffect(() => {
    Promise.all([fetchMockups(), fetchCollections(), fetchFolders(), fetchProductTypes()]).then(() => setLoading(false));
  }, [fetchMockups, fetchCollections, fetchFolders, fetchProductTypes]);

  // ---- Independent mockup folders ----
  function mockupFolderPath(id: string | null): MockupFolder[] {
    const path: MockupFolder[] = [];
    let cur = id ? folders.find((f) => f.id === id) : null;
    while (cur) { path.unshift(cur); cur = cur.parentId ? folders.find((f) => f.id === cur!.parentId) : null; }
    return path;
  }

  async function handleCreateFolder() {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await apiClient.createMockupFolder(name, currentFolderId, mockupKind);
      await fetchFolders();
      setNewFolderName("");
      setCreatingFolder(false);
    } catch { /* ignore */ }
  }

  async function handleRenameFolder(id: string, name: string) {
    try { await apiClient.updateMockupFolder(id, { name }); await fetchFolders(); } catch { /* ignore */ }
  }

  async function handleDeleteFolder(id: string) {
    if (!window.confirm("Klasör (ve alt klasörleri) silinsin mi? İçindeki mockuplar silinmez, köksüz kalır.")) return;
    try {
      await apiClient.deleteMockupFolder(id);
      if (currentFolderId === id) setCurrentFolderId(null);
      await Promise.all([fetchFolders(), fetchMockups()]);
    } catch { /* ignore */ }
  }

  async function handleMoveMockup(id: string, folderId: string | null) {
    try { await apiClient.updateMockup(id, { folderId }); await fetchMockups(); } catch { /* ignore */ }
  }

  const [dragOver, setDragOver] = useState(false);

  async function uploadFiles(files: FileList | File[]) {
    // Sonuc HER ZAMAN yazilir: "hicbir sey olmadi" ile "hata aldi" ayirt
    // edilemiyordu; bos secim de sessiz kaliyordu.
    if (!files || files.length === 0) {
      setUploadStatus("Dosya seçilmedi");
      return;
    }
    setUploading(true);
    setUploadError(null);
    setUploadStatus(`${files.length} dosya seçildi, yükleniyor…`);
    // Hatalar ONCEDEN sessizce yutuluyordu: ekranda "yukleniyor" yazip hicbir
    // sey olmuyordu. Artik dosya basina neden gorunur.
    const problems: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith("image/")) {
        problems.push(`${file.name}: görsel değil (${file.type || "tür bilinmiyor"})`);
        continue;
      }
      try {
        // Upload directly into the current folder (folderId), if any.
        // Tur AKTIF HATTA gore: taki magazasinda konsept, tekstilde mockup.
        // Aksi halde yuklenen gorsel "mockup" olarak kaydedilip konsept
        // listesinde hic gorunmuyordu.
        await apiClient.uploadMockup(file, file.name.replace(/\.[^.]+$/, ""), undefined, currentFolderId, mockupKind);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error("[upload] basarisiz:", file.name, err);
        problems.push(`${file.name}: ${msg}`);
      }
    }
    await fetchMockups();
    setUploading(false);
    setUploadError(problems.length ? problems.join(" · ") : null);
    setUploadStatus(`${files.length} dosya · ${files.length - problems.length} yüklendi · ${problems.length} hata`);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) uploadFiles(e.target.files);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) uploadFiles(e.dataTransfer.files);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
  }

  /** Konsept alanlarini guncelle (urun tipi, cozumleme, uretim talimati).
   *  AI ciktisi baslangic noktasi; son soz kullanicinin. */
  async function handleUpdateConcept(
    id: string,
    patch: { productTypeKey?: string | null; conceptAnalysis?: Record<string, unknown> | null; conceptPrompt?: string | null; sourceUrl?: string | null },
  ) {
    try {
      const res = await fetch(`${API_BASE}/api/mockups/${id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(patch),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || body.error || "Kaydedilemedi");
      setMockups((prev) => prev.map((m) => (m.id === id ? (body.data as CustomMockup) : m)));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Konsept güncellenemedi");
    }
  }

  /** Sahneyi AI ile cozumle — sonuc karta yazilir, elle duzeltilebilir */
  async function handleAnalyzeConcept(id: string, extraPrompt?: string) {
    try {
      const res = await fetch(`${API_BASE}/api/mockups/${id}/analyze-concept`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ extraPrompt }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || body.error || "Analiz edilemedi");
      setMockups((prev) => prev.map((m) => (m.id === id ? (body.data as CustomMockup) : m)));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Analiz edilemedi");
    }
  }

  async function handleDelete(id: string) {
    try {
      await apiClient.deleteMockup(id);
      setMockups((prev) => prev.filter((m) => m.id !== id));
    } catch { /* ignore */ }
  }

  async function handleUpdateField(id: string, field: string, value: string | number) {
    try {
      const updated = await apiClient.updateMockup(id, { [field]: value } as Record<string, string | number>);
      setMockups((prev) => prev.map((m) => (m.id === id ? updated : m)));
    } catch { /* ignore */ }
  }

  function handleOpacityChange(id: string, opacity: number) {
    setMockups((prev) => prev.map((m) => (m.id === id ? { ...m, opacity } : m)));
    if (opacityTimerRef.current) clearTimeout(opacityTimerRef.current);
    opacityTimerRef.current = setTimeout(() => handleUpdateField(id, "opacity", opacity), 300);
  }

  async function saveMockupAreas(mockupId: string, areas: AreaRect[]): Promise<CustomMockup | null> {
    try {
      const first = areas[0];
      const updated = await apiClient.updateMockup(mockupId, {
        designAreas: areas,
        // Single source of truth for light/dark: the area editor. Sync the
        // mockup theme to the first area's theme so the card/Studio reflect it.
        theme: (((first as any)?.theme as "light" | "dark" | undefined) ?? "light"),
        ...(first ? {
          designAreaX: Math.round(first.x),
          designAreaY: Math.round(first.y),
          designAreaW: Math.round(first.w),
          designAreaH: Math.round(first.h),
          designAreaRotation: Math.round(first.rotation || 0),
        } : {
          designAreaX: 0,
          designAreaY: 0,
          designAreaW: 0,
          designAreaH: 0,
          designAreaRotation: 0,
        }),
      });
      setMockups((prev) => prev.map((m) => (m.id === mockupId ? updated : m)));
      return updated;
    } catch (err) {
      console.error("Design area save failed:", err);
      alert("Alan kaydedilemedi: " + (err instanceof Error ? err.message : String(err)));
      return null;
    }
  }

  /** Renk tablosu "Tasarim bas" anahtari (eslesmis kopyalar da) */
  async function handleTogglePrintDesign(id: string, on: boolean) {
    setCharts((cs) => cs?.map((c) => (c.id === id ? ({ ...c, printDesign: on } as CustomMockup) : c)) ?? cs);
    try {
      const res = await fetch(`${API_BASE}/api/mockups/${id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ printDesign: on }) });
      if (!res.ok) throw new Error("Kaydedilemedi");
    } catch (err) { setUploadError(err instanceof Error ? err.message : "Kaydedilemedi"); setCharts(null); }
  }

  /** Renk tablosunu elle eslestir (target null = eslestirmeyi kaldir) */
  async function handleMatchChart(id: string, target: string | null) {
    if (target && !confirm("Bu iki tablo aynı tablo sayılacak: tek kart görünür ve alanları ortak kullanılır. Devam edilsin mi?")) return;
    try {
      const res = await fetch(`${API_BASE}/api/mockups/${id}/chart-match`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(target ? { targetId: target } : { unmatch: true }),
      });
      if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error || "Eşleştirilemedi");
      setCharts(null);
    } catch (err) { setUploadError(err instanceof Error ? err.message : "Eşleştirilemedi"); }
  }

  async function handleSaveDesignArea(mockupId: string, areas: AreaRect[]) {
    await saveMockupAreas(mockupId, areas);
    setConfiguringMockup(null);
    // Renk tablosu gorunumu ayri listeden: alani kaydedilen tablo yeniden cekilir
    if (chartMode) setCharts(null);
  }

  // Sample the average brightness of an area region to auto-pick light/dark.
  async function detectAreaTheme(imageUrl: string, area: AreaRect): Promise<"light" | "dark"> {
    const src = imageUrl.startsWith("http") ? imageUrl : `${API_BASE}${imageUrl}`;
    return new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const sx = Math.max(0, Math.round(area.x));
          const sy = Math.max(0, Math.round(area.y));
          const sw = Math.min(img.naturalWidth - sx, Math.round(area.w));
          const sh = Math.min(img.naturalHeight - sy, Math.round(area.h));
          if (sw <= 1 || sh <= 1) { resolve("light"); return; }
          const canvas = document.createElement("canvas");
          canvas.width = sw; canvas.height = sh;
          const ctx = canvas.getContext("2d");
          if (!ctx) { resolve("light"); return; }
          ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
          const data = ctx.getImageData(0, 0, sw, sh).data;
          let sum = 0, count = 0;
          const step = Math.max(4, Math.floor((sw * sh) / 2000) * 4); // sample ~2k px
          for (let i = 0; i < data.length; i += step) {
            sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            count++;
          }
          const avg = count ? sum / count : 255;
          // Tema = tasarimin rengi: koyu urune acik ("light") tasarim
          resolve(avg < 128 ? "light" : "dark");
        } catch { resolve("light"); }
      };
      img.onerror = () => resolve("light");
      img.src = src;
    });
  }

  // Bulk: auto-detect light/dark for every area-bearing mockup in the current
  // folder and save it — so the theme is correct without opening + saving each.
  async function handleAutoDetectThemes() {
    const hasArea = (m: CustomMockup) =>
      (Array.isArray((m as any).designAreas) && (m as any).designAreas.length > 0) ||
      (m.designAreaW > 20 && m.designAreaH > 20);
    const targets = mockups.filter((m) => (m.folderId ?? null) === currentFolderId && hasArea(m));
    if (targets.length === 0) return;
    setAutoDetecting(true);
    try {
      for (const m of targets) {
        const areas: any[] = Array.isArray((m as any).designAreas) ? (m as any).designAreas : [];
        const rect = areas[0]
          ? { x: areas[0].x, y: areas[0].y, w: areas[0].w, h: areas[0].h }
          : { x: m.designAreaX, y: m.designAreaY, w: m.designAreaW, h: m.designAreaH };
        const theme = await detectAreaTheme(m.imageUrl, rect as any);
        if (theme === ((areas[0]?.theme as string | undefined) ?? m.theme)) continue; // unchanged
        const nextAreas = areas.length ? areas.map((a, i) => (i === 0 ? { ...a, theme } : a)) : undefined;
        await apiClient.updateMockup(m.id, { theme, ...(nextAreas ? { designAreas: nextAreas } : {}) } as any);
      }
      await fetchMockups();
    } finally {
      setAutoDetecting(false);
    }
  }

  // Scale a source area set to a target image's dimensions, so coordinates stay
  // in the right spot even when the target mockup is a different size.
  function scaleAreas(areas: AreaRect[], fromW: number, fromH: number, toW: number, toH: number): AreaRect[] {
    // Yuzey maskesi kaynak fotografa ait: baska mockupa tasinmaz
    const own = areas.map(({ surfaceUrl: _s, ...a }) => a);
    if (!fromW || !fromH || (fromW === toW && fromH === toH)) return own;
    const sx = toW / fromW, sy = toH / fromH;
    return own.map((a) => ({ ...a, x: a.x * sx, y: a.y * sy, w: a.w * sx, h: a.h * sy }));
  }

  // Open the apply-areas picker: list mockups in the SAME folder (any size; the
  // coordinates are scaled per target) so the user can copy this area set to them
  // — especially the area-less ones. Each row gets an auto light/dark guess.
  async function handleSaveAndApplyToAll(areas: AreaRect[]) {
    const source = configuringMockup;
    if (!source || areas.length === 0) return;
    const hasAreas = (m: CustomMockup) =>
      (Array.isArray((m as any).designAreas) && (m as any).designAreas.length > 0) ||
      (m.designAreaW > 20 && m.designAreaH > 20);
    // Same folder only; size no longer required (areas are scaled to each target).
    const candidates = mockups.filter((m) => (m.folderId ?? null) === (source.folderId ?? null));
    const items = await Promise.all(candidates.map(async (m) => {
      const scaled = scaleAreas(areas, source.imageWidth, source.imageHeight, m.imageWidth, m.imageHeight);
      return {
        mockup: m,
        // default: select the source + any mockup that has no area yet (don't clobber configured ones)
        selected: m.id === source.id || !hasAreas(m),
        theme: await detectAreaTheme(m.imageUrl, scaled[0]),
      };
    }));
    items.sort((a, b) => (a.mockup.id === source.id ? -1 : b.mockup.id === source.id ? 1 : 0));
    setApplyFilterQ("");
    setApplyFilterArea("all");
    setApplyAreas({ areas, sourceW: source.imageWidth, sourceH: source.imageHeight, items });
    setConfiguringMockup(null);
  }

  // Apply the area set to the selected mockups, each with its chosen theme.
  async function handleApplyAreasConfirmed() {
    if (!applyAreas) return;
    const { areas, items, sourceW, sourceH } = applyAreas;
    const chosen = items.filter((i) => i.selected);
    try {
      for (const it of chosen) {
        const scaled = scaleAreas(areas, sourceW, sourceH, it.mockup.imageWidth, it.mockup.imageHeight);
        const mAreas = scaled.map((a) => ({ ...a, theme: it.theme }));
        const first = mAreas[0];
        const updated = await apiClient.updateMockup(it.mockup.id, {
          designAreas: mAreas,
          designAreaX: Math.round(first.x),
          designAreaY: Math.round(first.y),
          designAreaW: Math.round(first.w),
          designAreaH: Math.round(first.h),
          designAreaRotation: Math.round(first.rotation || 0),
          theme: it.theme,
        });
        setMockups((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      }
    } catch (err) {
      console.error("Apply-to-all failed:", err);
      alert("Toplu uygulama hatası: " + (err instanceof Error ? err.message : String(err)));
    }
    setApplyAreas(null);
  }

  // ---- Collection handlers ----

  async function handleCreateCollection() {
    const name = newCollectionName.trim();
    if (!name) return;
    try {
      await apiClient.createMockupCollection(name, undefined, null, mockupKind);
      await fetchCollections();
      setNewCollectionName("");
      setCreatingCollection(false);
    } catch { /* ignore */ }
  }

  // Tek mockup icin "satan mockup" isaretini ac/kapa
  async function handleToggleBigPrint(id: string) {
    const current = mockups.find((m) => m.id === id);
    const next = !current?.bigPrint;
    setMockups((prev) => prev.map((m) => (m.id === id ? { ...m, bigPrint: next } : m)));
    try {
      await apiClient.updateMockup(id, { bigPrint: next });
    } catch { fetchMockups(); }
  }

  async function handleBigPrintCopy(id: string) {
    try {
      await apiClient.createBigPrintCopy(id);
      await fetchMockups();
    } catch (err) {
      alert("Big print kopyası oluşturulamadı: " + (err instanceof Error ? err.message : String(err)));
    }
  }

  async function handleToggleSelling(id: string) {
    const current = mockups.find((m) => m.id === id);
    const next = !current?.isSelling;
    setMockups((prev) => prev.map((m) => (m.id === id ? { ...m, isSelling: next } : m)));
    try {
      await fetch(`${API_BASE}/api/mockups/${id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ isSelling: next }),
      });
    } catch { fetchMockups(); }
  }

  async function handleDeleteCollection(id: string) {
    try {
      await apiClient.deleteMockupCollection(id);
      setCollections((prev) => prev.filter((c) => c.id !== id));
    } catch { /* ignore */ }
  }

  async function handleRenameCollection(id: string, name: string) {
    try {
      await apiClient.updateMockupCollection(id, { name });
      await fetchCollections();
    } catch { /* ignore */ }
  }

  async function handleUpdateCollectionMockups(id: string, mockupIds: string[]) {
    try {
      await apiClient.updateMockupCollection(id, { mockupIds });
      await fetchCollections();
    } catch { /* ignore */ }
    setEditingCollection(null);
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {isConceptVertical ? "Konsept Fotoğrafları" : "Mockup Yönetimi"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isConceptVertical
              ? "Takı mağazası: sahnedeki ürün tedarikçi ürünüyle değiştirilir; tasarım alanı kullanılmaz"
              : "Mockup yükle, tasarım alanı belirle, koleksiyonlar oluştur"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowMockupLists(true)}
            className="flex items-center gap-2 rounded-lg bg-pink-50 px-4 py-2.5 text-sm font-medium text-pink-700 hover:bg-pink-100 transition-colors"
          >
            <Sparkles className="h-4 w-4" /> {NOUN} Listeleri
          </button>
          <Link
            to="/mockup-studio"
            className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Studio'ya Git <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" />Yükleniyor...</> : <><ImagePlus className="h-4 w-4" />{NOUN} Yükle</>}
          </button>
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={handleUpload} className="hidden" />

          <input ref={testDesignInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleTestDesignUpload} className="hidden" />
        </div>
      </div>

      {uploadStatus && !uploadError && (
        <div className="mb-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-600">
          {uploadStatus}
        </div>
      )}

      {uploadError && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <span>Yükleme başarısız — {uploadError}</span>
          <button onClick={() => setUploadError(null)} className="shrink-0 text-red-400 hover:text-red-600">kapat</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab("mockups")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            tab === "mockups" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <ImagePlus className="h-4 w-4" />
          {NOUNS} ({mockups.length})
        </button>
        <button
          onClick={() => setTab("collections")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            tab === "collections" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <Folder className="h-4 w-4" />
          Koleksiyonlar ({collections.length})
        </button>
        <button
          onClick={() => setTab("categories")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            tab === "categories" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <Layers className="h-4 w-4" />
          Kategoriler
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      ) : tab === "mockups" ? (
        /* ======== Mockups Tab ======== */
        (() => {
          const subfolders = folders.filter((f) => (f.parentId ?? null) === currentFolderId);
          const childCountOf = (id: string) => folders.filter((f) => f.parentId === id).length;
          const mockupCountOf = (id: string) => mockups.filter((m) => m.folderId === id).length;
          const path = mockupFolderPath(currentFolderId);
          const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
          const hasAreaOf = (m: CustomMockup) => (Array.isArray((m as any).designAreas) && (m as any).designAreas.length > 0) || (m.designAreaW > 20 && m.designAreaH > 20);
          const lq = norm(listFilterQ);
          // Arama/suzgec varken alt klasorlerin icindekiler de listelenir
          const analysisFiltering = !!listFilterAnalysis || !!listFilterPType || !!listFilterPres || !!listFilterCount;
          const filtering = !!lq || !!listFilterBrand || !!listFilterModel || !!listFilterColor || !!listFilterLabel || onlySelling || !!listFilterType || listFilterArea !== "all"
            || (!isConceptVertical && (groupByAnalysis || analysisFiltering));
          const scope = new Set<string | null>([currentFolderId]);
          if (filtering) {
            for (let grew = true; grew;) {
              grew = false;
              for (const f of folders) if (scope.has(f.parentId ?? null) && !scope.has(f.id)) { scope.add(f.id); grew = true; }
            }
          }
          const folderNameOf = (id: string | null | undefined) => folders.find((f) => f.id === id)?.name;
          const folderMockups = (chartMode ? charts ?? [] : mockups
            .filter((m) => (filtering ? currentFolderId === null || scope.has(m.folderId ?? null) : (m.folderId ?? null) === currentFolderId)))
            .filter((m) => {
              const okQ = !lq || norm(m.label).includes(lq);
              // Takida "alan var/yok" yerine "cozumlendi/cozumlenmedi"
              const analyzed = !!(m as unknown as { conceptAnalysis?: unknown }).conceptAnalysis;
              const has = isConceptVertical ? analyzed : hasAreaOf(m);
              const okBrand = !listFilterBrand || m.brand === listFilterBrand;
              const okModel = !listFilterModel || m.model === listFilterModel;
              const okColor = !listFilterColor || m.color === listFilterColor;
              const cl = m.colorLabel as { enabled?: boolean } | null | undefined;
              const labeled = !!cl && cl.enabled !== false;
              const okLabel = !listFilterLabel || (listFilterLabel === "has" ? labeled : !labeled);
              const okSelling = !onlySelling || !!m.isSelling;
              const okType = !listFilterType
                || (listFilterType === "__none__" ? !m.productTypeKey : m.productTypeKey === listFilterType);
              const ma = m.mockupAnalysis ?? null;
              const okAnalysis = isConceptVertical || (
                (!listFilterAnalysis || (listFilterAnalysis === "none" ? !ma : listFilterAnalysis === "analyzed" ? !!ma : ma?.status === listFilterAnalysis))
                && (!listFilterPType || ma?.productType === listFilterPType)
                && (!listFilterPres || ma?.presentation === listFilterPres)
                && (!listFilterCount || (!!ma && (listFilterCount === "5+" ? ma.items.length >= 5 : ma.items.length === Number(listFilterCount)))));
              return okQ && okBrand && okModel && okColor && okLabel && okSelling && okType && okAnalysis && (listFilterArea === "all" || (listFilterArea === "has" ? has : !has));
            });
          return (
            <div>
              {/* Breadcrumb + new folder */}
              <div className="mb-4 flex items-center gap-1 text-sm flex-wrap">
                <button onClick={() => setCurrentFolderId(null)}
                  className={`flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 ${currentFolderId === null ? "text-gray-900 font-medium" : "text-gray-500"}`}>
                  <Folder className="h-3.5 w-3.5" /> Tüm {NOUNS}
                </button>
                {path.map((f) => (
                  <span key={f.id} className="flex items-center gap-1">
                    <span className="text-gray-300">/</span>
                    <button onClick={() => setCurrentFolderId(f.id)}
                      className={`px-2 py-1 rounded hover:bg-gray-100 ${f.id === currentFolderId ? "text-gray-900 font-medium" : "text-gray-500"}`}>
                      {f.name}
                    </button>
                  </span>
                ))}
                {!creatingFolder && (
                  <button onClick={() => setCreatingFolder(true)}
                    className="ml-2 flex items-center gap-1 px-2 py-1 rounded text-indigo-600 hover:bg-indigo-50 text-xs font-medium">
                    <FolderPlus className="h-3.5 w-3.5" /> {currentFolderId ? "Alt Klasör" : "Klasör"}
                  </button>
                )}
              </div>
              {creatingFolder && (
                <div className="mb-4 flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3">
                  <input autoFocus value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleCreateFolder(); if (e.key === "Escape") setCreatingFolder(false); }}
                    placeholder={currentFolderId ? "Alt klasör adı (örn: Sweatshirt)" : "Klasör adı (örn: Comfort Colors)"}
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                  <button onClick={handleCreateFolder} disabled={!newFolderName.trim()}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
                    <Save className="h-4 w-4" /> Oluştur
                  </button>
                  <button onClick={() => setCreatingFolder(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4 text-gray-500" /></button>
                </div>
              )}

              {/* Filter: name search + area status */}
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input value={listFilterQ} onChange={(e) => setListFilterQ(e.target.value)}
                    placeholder="Dosya adı ara (or: lightgreen)"
                    className="w-full text-sm border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                </div>
                {/* Suzgec takida baska: tasarim alani yok, sahnenin cozumlenip
                    cozulmedigi ve hangi taki turune ait oldugu onemli. */}
                <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
                  {(isConceptVertical
                    ? ([["all", "Tümü"], ["none", "Çözümlenmedi"], ["has", "Çözümlendi"]] as const)
                    : ([["all", "Tümü"], ["none", "Alan yok"], ["has", "Alan var"]] as const)
                  ).map(([val, lbl]) => (
                    <button key={val} onClick={() => setListFilterArea(val)}
                      className={`px-2.5 py-1.5 ${listFilterArea === val ? "bg-indigo-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>{lbl}</button>
                  ))}
                </div>
                {!isConceptVertical && (
                  <>
                    <button onClick={toggleGroupByAnalysis} aria-pressed={groupByAnalysis}
                      title="Klasörlerden bağımsız: analizli mockuplar ürün tipi, sunum ve ürün sayısına göre gruplanır; analizsizler en altta"
                      className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium ${groupByAnalysis ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>
                      <Layers className="h-3.5 w-3.5" /> Analize göre grupla
                    </button>
                    <select aria-label="Analiz durumu" value={listFilterAnalysis} onChange={(e) => setListFilterAnalysis(e.target.value as typeof listFilterAnalysis)}
                      className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                      <option value="">Analiz: tümü</option>
                      <option value="analyzed">Analizli</option>
                      <option value="none">Analizsiz</option>
                      <option value="approved">Onaylı</option>
                      <option value="draft">Taslak</option>
                    </select>
                    <select aria-label="Ürün tipi" value={listFilterPType} onChange={(e) => setListFilterPType(e.target.value)}
                      className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                      <option value="">Tüm ürün tipleri</option>
                      {Object.entries(PRODUCT_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select aria-label="Sunum" value={listFilterPres} onChange={(e) => setListFilterPres(e.target.value)}
                      className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                      <option value="">Tüm sunumlar</option>
                      {Object.entries(PRESENTATION_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select aria-label="Ürün sayısı" value={listFilterCount} onChange={(e) => setListFilterCount(e.target.value)}
                      className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                      <option value="">Ürün sayısı</option>
                      {["1", "2", "3", "4", "5+"].map((n) => <option key={n} value={n}>{n} ürün</option>)}
                    </select>
                  </>
                )}
                {isConceptVertical && (
                  <select value={listFilterType} onChange={(e) => setListFilterType(e.target.value)}
                    className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                    <option value="">Tüm ürün tipleri</option>
                    {JEWELRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    <option value="__none__">Tipi girilmemiş</option>
                  </select>
                )}
                {/* Marka / Model / Renk filtresi (tedarikci katalogu varsa) */}
                {!isConceptVertical && supplierVars.hasCatalog && (
                  <>
                    <select value={listFilterBrand} onChange={(e) => { setListFilterBrand(e.target.value); setListFilterModel(""); setListFilterColor(""); }}
                      className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                      <option value="">Tüm markalar</option>
                      {supplierVars.brands.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                    <select value={listFilterModel} onChange={(e) => { setListFilterModel(e.target.value); setListFilterColor(""); }}
                      className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                      <option value="">Tüm modeller</option>
                      {supplierVars.modelsForBrand(listFilterBrand).map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <select value={listFilterColor} onChange={(e) => setListFilterColor(e.target.value)}
                      className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                      <option value="">Tüm renkler</option>
                      {supplierVars.colorsFor(listFilterBrand, listFilterModel).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </>
                )}
                {!isConceptVertical && (
                  <button onClick={() => setChartMode((v) => !v)} aria-pressed={chartMode}
                    title="Liste şablonlarındaki renk/beden tablosu görselleri (şablona eklenen tablo burada kendiliğinden görünür)"
                    className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 text-xs font-medium ${chartMode ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>
                    Color chart{chartMode && charts ? ` (${charts.length})` : ""}
                  </button>
                )}
                {!isConceptVertical && (
                  <select aria-label="Renk etiketi" title="Mockup üzerine basılan renk adı etiketi" value={listFilterLabel}
                    onChange={(e) => setListFilterLabel(e.target.value as "" | "has" | "none")}
                    className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                    <option value="">Renk etiketi: tümü</option>
                    <option value="has">Renk etiketi eklenmiş</option>
                    <option value="none">Renk etiketi eklenmemiş</option>
                  </select>
                )}
                {(listFilterQ || listFilterArea !== "all" || listFilterBrand || listFilterModel || listFilterColor || listFilterLabel) && (
                  <span className="text-xs text-gray-400">{folderMockups.length} sonuç</span>
                )}

                {/* Ornek tasarim (kompakt) + toplu acik/koyu algila */}
                <div className="flex items-center gap-1.5 ml-auto">
                  {folderMockups.some((m) => hasAreaOf(m)) && (
                    <button onClick={handleAutoDetectThemes} disabled={autoDetecting}
                      title="Klasördeki alanlı mockupların açık/koyusunu otomatik algıla ve kaydet"
                      className="flex items-center gap-1 text-[10px] font-medium text-gray-500 bg-white border border-gray-200 rounded-lg px-2 py-1.5 hover:border-indigo-300 hover:text-indigo-600 transition-colors disabled:opacity-50">
                      {autoDetecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      Açık/Koyu algıla
                    </button>
                  )}
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider pl-1">Örnek</span>
                  {(["light", "dark"] as const).map((mode) => {
                    const url = mode === "light" ? testDesignLight : testDesignDark;
                    const Icon = mode === "light" ? Sun : Moon;
                    const label = mode === "light" ? "Açık örnek" : "Koyu örnek";
                    return url ? (
                      <div key={mode} className="relative group">
                        <img src={url} alt={mode} title={label} className="h-7 w-7 rounded border border-gray-200 object-cover" />
                        <button
                          onClick={() => {
                            if (mode === "light") { setTestDesignLight(null); if (testDesignShared) setTestDesignDark(null); }
                            else { setTestDesignDark(null); if (testDesignShared) setTestDesignLight(null); }
                          }}
                          className="absolute -top-1.5 -right-1.5 bg-white border border-gray-200 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="h-2.5 w-2.5 text-red-500" />
                        </button>
                      </div>
                    ) : (
                      <button key={mode} title={`${label} yükle`}
                        onClick={() => { setTestUploadTarget(mode); testDesignInputRef.current?.click(); }}
                        className="h-7 w-7 flex items-center justify-center rounded border border-dashed border-gray-300 hover:border-indigo-400 transition-colors">
                        <Icon className={`h-3.5 w-3.5 ${mode === "light" ? "text-amber-500" : "text-gray-600"}`} />
                      </button>
                    );
                  })}
                  {(testDesignLight && testDesignDark) && (
                    <button
                      onClick={() => {
                        if (testDesignShared) { setTestDesignShared(false); }
                        else { const u = testDesignLight || testDesignDark; setTestDesignLight(u); setTestDesignDark(u); setTestDesignShared(true); }
                      }}
                      title={testDesignShared ? "Aynı tasarım (ayırmak için tıkla)" : "Farklı tasarım (birleştirmek için tıkla)"}
                      className={`text-[11px] font-semibold w-6 h-7 rounded border ${testDesignShared ? "bg-indigo-50 text-indigo-600 border-indigo-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                      {testDesignShared ? "=" : "≠"}
                    </button>
                  )}
                  {(testDesignLight || testDesignDark) && (
                    <button onClick={clearTestDesigns} title="Örnekleri temizle" className="p-1 text-gray-400 hover:text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Toplu islemler: secim + tema olustur + (katalog varsa) marka/model/renk atama */}
              {folderMockups.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50/40 p-2.5">
                  <button
                    onClick={() => {
                      const allSel = folderMockups.every((m) => bulkSel.has(m.id));
                      setBulkSel((prev) => { const n = new Set(prev); folderMockups.forEach((m) => { if (allSel) n.delete(m.id); else n.add(m.id); }); return n; });
                    }}
                    className="text-[11px] font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    {folderMockups.every((m) => bulkSel.has(m.id)) ? "Seçimi kaldır" : `Tümünü seç (${folderMockups.length})`}
                  </button>
                  <span className="text-[11px] text-gray-500">{bulkSel.size} seçili</span>

                  <button
                    onClick={() => setOnlySelling((v) => !v)}
                    title="Yalnızca satan mockup olarak işaretlenmişleri göster"
                    className={`flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-medium transition-colors ${
                      onlySelling
                        ? "border-amber-400 bg-amber-100 text-amber-800"
                        : "border-gray-200 bg-white text-gray-500 hover:border-amber-300"
                    }`}
                  >
                    <Star className={`h-3 w-3 ${onlySelling ? "fill-current" : ""}`} /> Satanlar
                  </button>

                  {/* Urun tipi ata — magaza dikeyine gore filtrelemeyi besler */}
                  <span className="ml-1 text-[11px] font-semibold text-indigo-700">Ürün tipi:</span>
                  <select
                    value={bulkProductType}
                    onChange={(e) => setBulkProductType(e.target.value)}
                    className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  >
                    <option value="">Seç…</option>
                    {productTypes.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    <option value="__clear__">— Kaldır —</option>
                  </select>
                  <button
                    onClick={async () => {
                      const ids = [...bulkSel];
                      if (ids.length === 0 || !bulkProductType) return;
                      const key = bulkProductType === "__clear__" ? null : bulkProductType;
                      setMockups((prev) => prev.map((m) => (bulkSel.has(m.id) ? { ...m, productTypeKey: key } : m)));
                      try {
                        await fetch(`${API_BASE}/api/mockups/bulk-variation`, {
                          method: "PATCH",
                          headers: authHeaders(),
                          body: JSON.stringify({ ids, productTypeKey: key }),
                        });
                      } catch { fetchMockups(); }
                      setBulkSel(new Set());
                      setBulkProductType("");
                    }}
                    disabled={bulkSel.size === 0 || !bulkProductType}
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Ata ({bulkSel.size})
                  </button>

                  {/* Satan mockup: uretilen listenin ANA gorseli olacak mockuplar */}
                  <button
                    onClick={async () => {
                      const ids = [...bulkSel];
                      if (ids.length === 0) return;
                      // Secililerin hepsi zaten satan ise kaldir, degilse isaretle
                      const allOn = mockups.filter((m) => bulkSel.has(m.id)).every((m) => m.isSelling);
                      const next = !allOn;
                      setMockups((prev) => prev.map((m) => (bulkSel.has(m.id) ? { ...m, isSelling: next } : m)));
                      try {
                        await fetch(`${API_BASE}/api/mockups/bulk-variation`, {
                          method: "PATCH",
                          headers: authHeaders(),
                          body: JSON.stringify({ ids, isSelling: next }),
                        });
                      } catch { fetchMockups(); }
                      setBulkSel(new Set());
                    }}
                    disabled={bulkSel.size === 0}
                    title="Satan mockup: üretilen listenin ana görseli olur, eklentideki seçim listesinde çıkar"
                    className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50"
                  >
                    <Star className="h-3.5 w-3.5" /> Satan Mockup ({bulkSel.size})
                  </button>

                  {/* Giyim mockup analizi (AI, arka planda sirayla) */}
                  {!isConceptVertical && (
                    <button onClick={handleBulkAnalyze} disabled={bulkSel.size === 0}
                      title="Seçili mockupların ürün tipi, sunum, ürün renk/ton/yüz ve tema analizini AI ile çıkar (taslak olarak kaydedilir)"
                      className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                      <Sparkles className="h-3.5 w-3.5 text-indigo-600" /> AI ile analiz et ({bulkSel.size})
                    </button>
                  )}

                  {/* Tema Olustur (katalog gerekmez) */}
                  <button onClick={() => setThemeModalSources(folderMockups.filter((m) => bulkSel.has(m.id)))}
                    disabled={bulkSel.size === 0}
                    className="flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-700 disabled:opacity-50">
                    <Sparkles className="h-3.5 w-3.5" /> Tema Oluştur ({bulkSel.size})
                  </button>

                  {/* Marka/model/renk atama — sadece katalog varsa */}
                  {supplierVars.hasCatalog && (
                    <>
                      <span className="ml-1 text-[11px] font-semibold text-indigo-700">Ata:</span>
                      <select value={bulkBrand} onChange={(e) => { setBulkBrand(e.target.value); setBulkModel(""); setBulkColor(""); }}
                        className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                        <option value="">Marka</option>
                        {supplierVars.brands.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                      <select value={bulkModel} onChange={(e) => { setBulkModel(e.target.value); setBulkColor(""); }}
                        className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                        <option value="">Model</option>
                        {supplierVars.modelsForBrand(bulkBrand).map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <select value={bulkColor} onChange={(e) => setBulkColor(e.target.value)}
                        className="text-xs text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-200 focus:outline-none">
                        <option value="">Renk</option>
                        {supplierVars.colorsFor(bulkBrand, bulkModel).map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <button onClick={applyBulkVariation} disabled={bulkSel.size === 0 || (!bulkBrand && !bulkModel && !bulkColor) || bulkApplying}
                        className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                        {bulkApplying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Uygula
                      </button>
                    </>
                  )}
                  {bulkSel.size > 0 && <button onClick={() => setBulkSel(new Set())} className="text-[11px] text-gray-500 hover:text-gray-700">Temizle</button>}
                </div>
              )}

              {!isConceptVertical && groupByAnalysis ? (
                <div className="space-y-6">
                  {(() => {
                    const groups = new Map<string, { label: string; items: CustomMockup[] }>();
                    for (const m of folderMockups) {
                      const a = m.mockupAnalysis;
                      const key = a ? `${a.productType}|${a.presentation}|${a.items.length}` : "__none__";
                      const label = a ? `${PRODUCT_TYPE_LABEL[a.productType] ?? a.productType} · ${PRESENTATION_LABEL[a.presentation] ?? a.presentation} · ${a.items.length} ürün` : "Analiz yok";
                      if (!groups.has(key)) groups.set(key, { label, items: [] });
                      groups.get(key)!.items.push(m);
                    }
                    const ordered = [...groups.entries()].sort((x, y) => (x[0] === "__none__" ? 1 : y[0] === "__none__" ? -1 : y[1].items.length - x[1].items.length));
                    if (!ordered.length) return <p className="text-sm text-gray-500">Süzgece uyan mockup yok.</p>;
                    return ordered.map(([key, g]) => {
                      const closed = collapsedGroups.has(key);
                      const toggle = () => setCollapsedGroups((cur) => { const n = new Set(cur); if (n.has(key)) n.delete(key); else n.add(key); return n; });
                      return (
                        <section key={key}>
                          <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-2">
                            <button onClick={toggle} aria-expanded={!closed} className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                              <ChevronDown className={`h-4 w-4 transition-transform ${closed ? "-rotate-90" : ""}`} /> {g.label}
                            </button>
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{g.items.length}</span>
                            {key !== "__none__" && <span className="text-xs text-gray-400">{g.items.filter((m) => m.mockupAnalysis?.status === "approved").length} onaylı</span>}
                            <button onClick={() => setBulkSel((cur) => { const n = new Set(cur); g.items.forEach((m) => n.add(m.id)); return n; })}
                              className="ml-auto rounded-lg px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50">
                              {key === "__none__" ? "Grubu seç (sonra AI ile analiz et)" : "Grubu seç"}
                            </button>
                          </div>
                          {!closed && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">{g.items.map((m) => renderCard(m))}</div>}
                        </section>
                      );
                    });
                  })()}
                </div>
              ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Subfolders (open / rename / delete) */}
                {subfolders.map((f) => (
                  <div key={f.id} className="rounded-xl border-2 border-amber-100 bg-amber-50/40 flex flex-col min-h-[340px] hover:border-amber-300 transition-all overflow-hidden">
                    <button onClick={() => setCurrentFolderId(f.id)} className="flex-1 flex flex-col items-center justify-center gap-2 hover:bg-amber-50/70 transition-colors">
                      <Folder className="h-12 w-12 text-amber-400" />
                      <span className="text-sm font-medium text-gray-700 px-3 text-center">{f.name}</span>
                      <span className="text-xs text-gray-400">{childCountOf(f.id) > 0 ? `${childCountOf(f.id)} klasör · ` : ""}{mockupCountOf(f.id)} mockup</span>
                    </button>
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-amber-100 bg-white/70">
                      <InlineEdit value={f.name} onSave={(name) => handleRenameFolder(f.id, name)} />
                      <button onClick={() => handleDeleteFolder(f.id)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 flex-shrink-0" title="Klasörü sil"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
                {/* Upload (into current folder) */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
                  disabled={uploading}
                  className={`rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-3 py-16 transition-all disabled:opacity-50 group min-h-[340px] ${
                    dragOver ? "border-indigo-500 bg-indigo-50 scale-[1.02] shadow-lg" : "border-gray-200 bg-gray-50 hover:border-indigo-300 hover:bg-indigo-50/50"
                  }`}
                >
                  {uploading ? <Loader2 className="h-8 w-8 animate-spin text-indigo-400" /> : dragOver ? <ImagePlus className="h-10 w-10 text-indigo-500 animate-bounce" /> : <Plus className="h-8 w-8 text-gray-300 group-hover:text-indigo-400 transition-colors" />}
                  <span className={`text-sm font-medium transition-colors ${dragOver ? "text-indigo-600" : "text-gray-500 group-hover:text-indigo-600"}`}>
                    {uploading ? "Yükleniyor..." : dragOver ? "Bırakın, yüklenecek!" : `Yeni ${NOUN} Yükle`}
                  </span>
                  {!uploading && !dragOver && <span className="text-xs text-gray-400">{currentFolderId ? "bu klasöre yüklenir" : "veya sürükle bırak"}</span>}
                </button>
                {/* Mockups in current folder */}
                {folderMockups.map((m) => renderCard(m))}
              </div>
              )}
            </div>
          );
          function renderCard(m: CustomMockup) {
            return (
                  <div key={m.id} className="flex flex-col">
                  {filtering && (m.folderId ?? null) !== currentFolderId && (
                    <span className="mb-1 flex items-center gap-1 truncate text-[11px] text-gray-500" title="Bulunduğu klasör">
                      <Folder className="h-3 w-3 shrink-0" /> {folderNameOf(m.folderId) ?? "Klasörsüz"}
                    </span>
                  )}
                  <MockupCard
                    mockup={m}
                    folders={folders}
                    onMove={(folderId) => handleMoveMockup(m.id, folderId)}
                    onConfigure={() => setConfiguringMockup(m)}
                    onEditOcclusion={() => setOcclusionMockup(m)}
                    onDelete={() => handleDelete(m.id)}
                    onUpdateLabel={(label) => handleUpdateField(m.id, "label", label)}
                    onUpdateCategory={(cat) => handleUpdateField(m.id, "category", cat)}
                    onUpdateOpacity={(opacity) => handleOpacityChange(m.id, opacity)}
                    testDesignLight={testDesignLight}
                    testDesignDark={testDesignDark}
                    vars={supplierVars}
                    onAssignVariation={(patch) => handleAssignVariation(m.id, patch)}
                    selectable
                    selected={bulkSel.has(m.id)}
                    onToggleSelect={() => toggleBulk(m.id)}
                    onToggleSelling={() => handleToggleSelling(m.id)}
                    onToggleBigPrint={() => handleToggleBigPrint(m.id)}
                    onBigPrintCopy={() => handleBigPrintCopy(m.id)}
                    isConcept={isConceptVertical}
                    conceptTypes={productTypes.map((p) => p.key)}
                    onUpdateConcept={(patch) => handleUpdateConcept(m.id, patch)}
                    onAnalyzeConcept={(extra) => handleAnalyzeConcept(m.id, extra)}
                    onOpenAnalysis={() => setAnalysisMockup(m)}
                    {...(chartMode ? {
                      chartPeers: (charts ?? []).filter((c) => c.id !== m.id).map((c) => ({ id: c.id, label: c.label })),
                      onMatchChart: (target: string | null) => void handleMatchChart(m.id, target),
                      onTogglePrintDesign: (on: boolean) => void handleTogglePrintDesign(m.id, on),
                    } : {})}
                  />
                  </div>
            );
          }
        })()
      ) : (
        tab === "categories" ? (
        /* ======== Kategoriler: kategori -> listing sablonu + mockup grubu ======== */
        <CategorySettings />
      ) : (
        /* ======== Collections Tab (flat list) ======== */
        <div>
          {/* Toplu urun tipi atama — birden fazla grubu tek seferde etiketle */}
          {selectedCollectionIds.size > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 p-3">
              <span className="text-sm font-medium text-indigo-800">
                {selectedCollectionIds.size} grup seçili
              </span>
              <select
                value={bulkType}
                onChange={(e) => setBulkType(e.target.value)}
                className="rounded-lg border border-indigo-200 bg-white px-2 py-1.5 text-xs"
              >
                <option value="">Ürün tipi seç…</option>
                {productTypes.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                <option value="__clear__">— Tipi kaldır —</option>
              </select>
              <button
                onClick={async () => {
                  const key = bulkType === "__clear__" ? "" : bulkType;
                  if (!bulkType) return;
                  const ids = [...selectedCollectionIds];
                  // Iyimser guncelleme
                  setCollections((prev) => prev.map((c) => (ids.includes(c.id) ? { ...c, productTypeKey: key || null } : c)));
                  try {
                    await fetch(`${API_BASE}/api/mockups/collections/bulk-product-type`, {
                      method: "PATCH",
                      headers: authHeaders(),
                      body: JSON.stringify({ ids, productTypeKey: key || null }),
                    });
                  } catch { /* ag hatasi — sonraki yuklemede sunucudaki deger gecerli */ }
                  setSelectedCollectionIds(new Set());
                  setBulkType("");
                }}
                disabled={!bulkType}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                Uygula
              </button>
              <button
                onClick={() => setSelectedCollectionIds(new Set())}
                className="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs text-indigo-700 hover:bg-indigo-100"
              >
                Seçimi temizle
              </button>
              <button
                onClick={() => setSelectedCollectionIds(new Set(collections.map((c) => c.id)))}
                className="ml-auto text-xs text-indigo-600 hover:underline"
              >
                Tümünü seç ({collections.length})
              </button>
            </div>
          )}

          {/* Create collection */}
          <div className="mb-5">
            {creatingCollection ? (
              <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4">
                <input autoFocus value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleCreateCollection(); if (e.key === "Escape") setCreatingCollection(false); }}
                  placeholder="Koleksiyon adı (örn: Anneler Günü)"
                  className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
                <button onClick={handleCreateCollection} disabled={!newCollectionName.trim()}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
                  <Save className="h-4 w-4" /> Oluştur
                </button>
                <button onClick={() => setCreatingCollection(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4 text-gray-500" /></button>
              </div>
            ) : (
              <button onClick={() => setCreatingCollection(true)}
                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors">
                <FolderPlus className="h-4 w-4" /> Yeni Koleksiyon
              </button>
            )}
          </div>

          {collections.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50">
              <Folder className="h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-base font-semibold text-gray-700 mb-1">Henüz koleksiyon yok</h3>
              <p className="text-sm text-gray-500 mb-4 max-w-sm text-center">Koleksiyon oluşturup mockupları gruplayın. "Mockup Seç" ile mockup ekleyip çıkarabilirsin.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {collections.map((c) => (
                <CollectionCard
                  key={c.id}
                  collection={c}
                  allMockups={mockups}
                  onDelete={() => handleDeleteCollection(c.id)}
                  onRename={(name) => handleRenameCollection(c.id, name)}
                  onEditMockups={() => setEditingCollection(c)}
                  productTypes={productTypes}
                  onSetProductType={(key) => setCollectionProductType(c.id, key)}
                  selected={selectedCollectionIds.has(c.id)}
                  onToggleSelect={() => setSelectedCollectionIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                    return next;
                  })}
                />
              ))}
            </div>
          )}
        </div>
        )
      )}

      {/* Design Area Editor Modal */}
      {configuringMockup && (
        <DesignAreaEditor
          mockup={configuringMockup}
          onSave={(areas) => handleSaveDesignArea(configuringMockup.id, areas)}
          onSaveAndApplyAll={handleSaveAndApplyToAll}
          onCancel={() => setConfiguringMockup(null)}
          testDesignLight={testDesignLight}
          testDesignDark={testDesignDark}
          {...(!isConceptVertical ? {
            apparel: {
              vars: supplierVars,
              brandOptions: [...new Set(mockups.map((m) => m.brand).filter((b): b is string => !!b))],
              onSaved: (u: CustomMockup) => setMockups((prev) => prev.map((m) => (m.id === u.id ? { ...m, ...u } : m))),
            },
          } : {})}
        />
      )}

      {analysisMockup && (
        <MockupAnalysisModal
          mockup={analysisMockup}
          vars={supplierVars}
          brandOptions={[...new Set(mockups.map((m) => m.brand).filter((b): b is string => !!b))]}
          onClose={() => setAnalysisMockup(null)}
          onSaved={(u) => {
            setMockups((prev) => prev.map((m) => (m.id === u.id ? { ...m, ...u } : m)));
            // Sablon kurma penceresi aciksa o da guncel analizi gostersin
            setConfiguringMockup((c) => (c && c.id === u.id ? { ...c, ...u } : c));
          }}
        />
      )}

      {/* Occlusion (on plan) Editor Modal */}
      {occlusionMockup && (
        <OcclusionEditor
          mockup={occlusionMockup}
          designUrl={occlusionMockup.theme === "dark" ? (testDesignDark || testDesignLight) : (testDesignLight || testDesignDark)}
          onClose={() => setOcclusionMockup(null)}
          onSaved={(updated) => { setMockups((prev) => prev.map((m) => (m.id === updated.id ? updated : m))); }}
        />
      )}

      {/* Apply Areas Picker Modal */}
      {applyAreas && (() => {
        const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
        const hasAreaOf = (m: CustomMockup) => (Array.isArray((m as any).designAreas) && (m as any).designAreas.length > 0) || (m.designAreaW > 20 && m.designAreaH > 20);
        const q = norm(applyFilterQ);
        const filtered = applyAreas.items
          .map((it, idx) => ({ it, idx }))
          .filter(({ it }) => {
            const okQ = !q || norm(it.mockup.label).includes(q);
            const has = hasAreaOf(it.mockup);
            const okArea = applyFilterArea === "all" || (applyFilterArea === "has" ? has : !has);
            return okQ && okArea;
          });
        const setSelectedForFiltered = (value: boolean) => setApplyAreas((s) => {
          if (!s) return s;
          const ids = new Set(filtered.map(({ idx }) => idx));
          return { ...s, items: s.items.map((i, idx) => ids.has(idx) ? { ...i, selected: value } : i) };
        });
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Alanları Uygula</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Aynı dosyadaki {applyAreas.items.length} mockup (boyut farklı olsa da ölçek) • {applyAreas.items.filter((i) => i.selected).length} seçili
                </p>
              </div>
              <button onClick={() => setApplyAreas(null)} className="p-2 rounded-lg hover:bg-gray-100"><X className="h-5 w-5 text-gray-500" /></button>
            </div>

            <div className="px-5 py-2.5 border-b border-gray-100 flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input value={applyFilterQ} onChange={(e) => setApplyFilterQ(e.target.value)}
                  placeholder="Dosya adı ara (or: lightgreen)"
                  className="w-full text-sm border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 focus:ring-2 focus:ring-indigo-200 focus:outline-none" />
              </div>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
                {([["all", "Tümü"], ["none", "Alan yok"], ["has", "Alan var"]] as const).map(([val, lbl]) => (
                  <button key={val} onClick={() => setApplyFilterArea(val)}
                    className={`px-2.5 py-1.5 ${applyFilterArea === val ? "bg-indigo-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>{lbl}</button>
                ))}
              </div>
            </div>
            <div className="px-5 py-1.5 border-b border-gray-100 flex items-center gap-2 text-xs">
              <span className="text-gray-400">{filtered.length} gösteriliyor</span>
              <span className="text-gray-300 ml-auto">|</span>
              <button onClick={() => setSelectedForFiltered(true)} className="text-indigo-600 hover:underline">Tümünü seç</button>
              <span className="text-gray-300">|</span>
              <button onClick={() => setSelectedForFiltered(false)} className="text-gray-500 hover:underline">Tümünü kaldır</button>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-2">
              {filtered.length === 0 ? (
                <p className="text-center text-gray-500 py-10">{applyAreas.items.length === 0 ? "Aynı dosyada başka mockup yok." : "Filtreye uyan mockup yok."}</p>
              ) : filtered.map(({ it, idx }) => {
                const m = it.mockup;
                const _tu = m.thumbnailUrl || m.imageUrl; const url = _tu.startsWith("http") ? _tu : `${API_BASE}${_tu}`;
                const ownAreas: AreaRect[] = (() => {
                  const da = (m as any).designAreas;
                  if (Array.isArray(da) && da.length > 0) return da;
                  if (m.designAreaW > 20 && m.designAreaH > 20) return [{ id: "legacy", x: m.designAreaX, y: m.designAreaY, w: m.designAreaW, h: m.designAreaH, rotation: m.designAreaRotation || 0, label: "on" }];
                  return [];
                })();
                const has = ownAreas.length > 0;
                // Selected -> shows the common areas (scaled to this mockup's size)
                // that will be applied; otherwise its own (edited) areas.
                const commonScaled = scaleAreas(applyAreas.areas, applyAreas.sourceW, applyAreas.sourceH, m.imageWidth, m.imageHeight);
                const previewAreas = it.selected ? commonScaled : (has ? ownAreas : commonScaled);
                return (
                  <div key={m.id} className={`flex items-center gap-3 rounded-xl border p-2.5 ${it.selected ? "border-indigo-300 bg-indigo-50/40" : "border-gray-200"}`}>
                    <input type="checkbox" checked={it.selected}
                      onChange={(e) => setApplyAreas((s) => s ? { ...s, items: s.items.map((x, i) => i === idx ? { ...x, selected: e.target.checked } : x) } : s)}
                      className="rounded border-gray-300" />
                    <div className="relative flex-shrink-0 rounded-lg border border-gray-100 bg-gray-50 overflow-hidden flex items-center justify-center" style={{ width: 176, height: 176 }}>
                      <div className="relative" style={{ aspectRatio: `${m.imageWidth} / ${m.imageHeight}`, ...(m.imageWidth >= m.imageHeight ? { width: "100%" } : { height: "100%" }) }}>
                        <img src={url} alt={m.label} className="absolute inset-0 h-full w-full object-contain" crossOrigin="anonymous" />
                        {previewAreas.map((area, ai) => hasWarp(area) ? (
                          <WarpedAreaOutline key={ai} area={area} imageW={m.imageWidth} imageH={m.imageHeight}
                            label={areaLabelText(area.label).toUpperCase()} light={it.theme === "light"} />
                        ) : (
                          <div key={ai} className="absolute border-2 border-dashed border-indigo-400 flex items-center justify-center overflow-hidden"
                            style={{
                              left: `${(area.x / m.imageWidth) * 100}%`,
                              top: `${(area.y / m.imageHeight) * 100}%`,
                              width: `${(area.w / m.imageWidth) * 100}%`,
                              height: `${(area.h / m.imageHeight) * 100}%`,
                              transform: area.rotation ? `rotate(${area.rotation}deg)` : undefined,
                              transformOrigin: "top left",
                            }}>
                            <span className="font-bold leading-none text-center px-0.5" style={{
                              fontSize: 11,
                              color: it.theme === "light" ? "#ffffff" : "#000000",
                              textShadow: it.theme === "light" ? "0 0 3px rgba(0,0,0,0.7)" : "0 0 3px rgba(255,255,255,0.7)",
                            }}>{areaLabelText(area.label).toUpperCase()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{m.label}</div>
                      <div className="text-[10px]">{has ? <span className="text-amber-600">alan var (üzerine yazılır)</span> : <span className="text-gray-400">alan yok</span>}</div>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <div className="flex rounded-lg border border-gray-200 overflow-hidden text-[11px] font-medium">
                        <button onClick={() => setApplyAreas((s) => s ? { ...s, items: s.items.map((x, i) => i === idx ? { ...x, theme: "light" } : x) } : s)}
                          className={`px-2.5 py-1 ${it.theme === "light" ? "bg-white text-gray-900" : "bg-gray-50 text-gray-400"}`}>Açık</button>
                        <button onClick={() => setApplyAreas((s) => s ? { ...s, items: s.items.map((x, i) => i === idx ? { ...x, theme: "dark" } : x) } : s)}
                          className={`px-2.5 py-1 ${it.theme === "dark" ? "bg-gray-800 text-white" : "bg-gray-50 text-gray-400"}`}>Koyu</button>
                      </div>
                      <button onClick={() => setApplyEditMockup(m)}
                        title="Bu mockup'ın alanını ayrı düzenle"
                        className="flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded hover:bg-indigo-50">
                        <Settings2 className="h-3.5 w-3.5" /> Düzenle
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-3 p-5 border-t border-gray-100">
              <button onClick={() => setApplyAreas(null)} className="px-5 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
              <button onClick={handleApplyAreasConfirmed} disabled={applyAreas.items.filter((i) => i.selected).length === 0}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
                <Copy className="h-4 w-4" /> {applyAreas.items.filter((i) => i.selected).length} mockup'a uygula
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Edit one mockup's area from the apply modal (seeded with the applied areas) */}
      {applyEditMockup && applyAreas && (
        <DesignAreaEditor
          mockup={applyEditMockup}
          seedAreas={scaleAreas(applyAreas.areas, applyAreas.sourceW, applyAreas.sourceH, applyEditMockup.imageWidth, applyEditMockup.imageHeight)}
          backLabel="← Listeye Dön"
          testDesignLight={testDesignLight}
          testDesignDark={testDesignDark}
          onSave={async (areas) => {
            const updated = await saveMockupAreas(applyEditMockup.id, areas);
            setApplyAreas((s) => s ? {
              ...s,
              items: s.items.map((it) => it.mockup.id === applyEditMockup.id
                ? { ...it, selected: false, mockup: updated ?? it.mockup }
                : it),
            } : s);
            setApplyEditMockup(null);
          }}
          onCancel={() => setApplyEditMockup(null)}
        />
      )}

      {/* Collection Mockup Selector Modal */}
      {editingCollection && (
        <CollectionMockupSelector
          collection={editingCollection}
          allMockups={mockups}
          folders={folders}
          onSave={(ids) => handleUpdateCollectionMockups(editingCollection.id, ids)}
          onCancel={() => setEditingCollection(null)}
          onSaveAreas={saveMockupAreas}
          vars={supplierVars}
          onAssignVariation={handleAssignVariation}
          noun={NOUN}
        />
      )}

      {showMockupLists && (
        <MockupListsModal onClose={() => setShowMockupLists(false)} onCreated={() => { fetchMockups(); }} />
      )}

      {themeModalSources && themeModalSources.length > 0 && (
        <MockupThemeModal
          sources={themeModalSources}
          onClose={() => setThemeModalSources(null)}
          onCreated={() => fetchMockups()}
        />
      )}
    </div>
  );
}

/** Carpitilmis alanin cercevesi (render eslemesinin kenari ornekleniyor) */
function WarpedAreaOutline({ area, imageW, imageH, label, light }: { area: AreaRect; imageW: number; imageH: number; label: string; light: boolean }) {
  const map = warpMapOf(area);
  const N = 24;
  const pts: [number, number][] = [];
  for (let i = 0; i < N; i++) pts.push(map.forward(i / N, 0));
  for (let i = 0; i < N; i++) pts.push(map.forward(1, i / N));
  for (let i = N; i > 0; i--) pts.push(map.forward(i / N, 1));
  for (let i = N; i > 0; i--) pts.push(map.forward(0, i / N));
  const [cx, cy] = map.forward(0.5, 0.5);
  return (
    <>
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox={`0 0 ${imageW} ${imageH}`} preserveAspectRatio="none">
        <polygon points={pts.map((p) => p.join(",")).join(" ")} fill="none" stroke="#818cf8" strokeWidth={2} strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
      </svg>
      <span className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 font-bold leading-none" style={{
        left: `${(cx / imageW) * 100}%`, top: `${(cy / imageH) * 100}%`, fontSize: 11,
        color: light ? "#ffffff" : "#000000",
        textShadow: light ? "0 0 3px rgba(0,0,0,0.7)" : "0 0 3px rgba(255,255,255,0.7)",
      }}>{label}</span>
    </>
  );
}
